// src/pages/UserManager.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Button, Spinner, Modal, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';

// ── Design tokens ─────────────────────────────────────────────────────────────
const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

const AVATAR_PALETTE = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#ef4444','#84cc16'];
function nameToColor(name) {
    if (!name) return '#9ca3af';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[1][0]).toUpperCase();
}

const ROLE_LABEL = { user: 'Usuario', authorizer: 'Autorizador' };
const ROLE_COLOR = { user: '#6b7280', authorizer: '#0891b2' };
const ROLE_BG    = { user: '#f3f4f6', authorizer: '#ecfeff' };

function RolePill({ role }) {
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 20,
            background: ROLE_BG[role] || '#f3f4f6',
            color: ROLE_COLOR[role] || '#6b7280',
            fontWeight: 600, fontSize: 11,
        }}>
            {ROLE_LABEL[role] || role}
        </span>
    );
}

// ── Modal de edición ──────────────────────────────────────────────────────────
function EditModal({ user, onClose, onSaved }) {
    const [name,        setName]        = useState(user.name);
    const [role,        setRole]        = useState(user.role);
    const [active,      setActive]      = useState(user.active);
    const [depts,       setDepts]       = useState([]);
    const [deptsError,  setDeptsError]  = useState(false);
    const [selectedDepts, setSelectedDepts] = useState(user.departments?.map(d => d._id || d) || []);
    // Departamentos extra a los que un solicitante puede levantar tickets
    // (se suman a la política de su unidad de negocio).
    const [requestDepts,  setRequestDepts]  = useState(user.requestDepartments?.map(d => d._id || d) || []);
    const [saving,      setSaving]      = useState(false);

    useEffect(() => {
        api.get('/departments')
            .then(r => setDepts(r.data))
            .catch(() => setDeptsError(true));
    }, []);

    const toggleDept = (id) => {
        setSelectedDepts(prev =>
            prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
        );
    };
    const toggleRequestDept = (id) => {
        setRequestDepts(prev =>
            prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
        );
    };

    const handleSave = async () => {
        if (!name.trim()) { toast.warn('El nombre es obligatorio'); return; }
        if (role === 'agent' && selectedDepts.length === 0) {
            toast.warn('Asigna al menos un departamento para el agente');
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/users/${user._id}`, {
                name:        name.trim(),
                role,
                active,
                departments: (role === 'agent' || role === 'authorizer') ? selectedDepts : [],
                requestDepartments: role === 'user' ? requestDepts : [],
            });
            const wasPromoted = user.role !== 'agent' && role === 'agent';
            toast.success(wasPromoted
                ? `${data.name} promovido a agente — ya aparece en Gestión de Equipo`
                : 'Usuario actualizado');
            onSaved(data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al guardar');
        } finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontWeight: 700, fontSize: 16 }}>Editar usuario — {user.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Nombre</Form.Label>
                    <Form.Control
                        value={name}
                        onChange={e => setName(e.target.value)}
                        style={{ borderRadius: 8 }}
                    />
                </Form.Group>

                <Row className="mb-3">
                    <Col>
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Rol / Permisos</Form.Label>
                        <Form.Select value={role} onChange={e => setRole(e.target.value)} style={{ borderRadius: 8 }}>
                            <option value="user">Usuario — solo crea y consulta sus tickets</option>
                            <option value="authorizer">Autorizador — puede aprobar o rechazar tickets</option>
                            <option value="agent">Agente — gestiona y responde tickets</option>
                        </Form.Select>
                    </Col>
                </Row>

                {(role === 'agent' || role === 'authorizer') && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Departamentos</Form.Label>
                            <div style={{
                                border: '1px solid var(--c-border)', borderRadius: 8,
                                padding: '8px 12px', maxHeight: 160, overflowY: 'auto',
                                background: 'var(--c-input-bg)',
                            }}>
                                {deptsError
                                    ? <span style={{ fontSize: 12, color: '#dc2626' }}>Error al cargar departamentos — recarga la página</span>
                                    : depts.length === 0
                                    ? <span style={{ fontSize: 12, color: 'var(--c-text-4)' }}>Cargando...</span>
                                    : depts.map(d => (
                                        <Form.Check
                                            key={d._id}
                                            type="checkbox"
                                            label={d.name}
                                            checked={selectedDepts.includes(d._id)}
                                            onChange={() => toggleDept(d._id)}
                                            style={{ fontSize: 13 }}
                                        />
                                    ))
                                }
                            </div>
                            <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                                {role === 'authorizer'
                                    ? 'El autorizador aparecerá disponible para estos departamentos.'
                                    : 'El agente verá los tickets de los departamentos asignados.'}
                            </Form.Text>
                        </Col>
                    </Row>
                )}

                {role === 'user' && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Departamentos adicionales para levantar tickets</Form.Label>
                            <div style={{
                                border: '1px solid var(--c-border)', borderRadius: 8,
                                padding: '8px 12px', maxHeight: 160, overflowY: 'auto',
                                background: 'var(--c-input-bg)',
                            }}>
                                {deptsError
                                    ? <span style={{ fontSize: 12, color: '#dc2626' }}>Error al cargar departamentos — recarga la página</span>
                                    : depts.length === 0
                                    ? <span style={{ fontSize: 12, color: 'var(--c-text-4)' }}>Cargando...</span>
                                    : depts.map(d => (
                                        <Form.Check
                                            key={d._id}
                                            type="checkbox"
                                            label={d.name}
                                            checked={requestDepts.includes(d._id)}
                                            onChange={() => toggleRequestDept(d._id)}
                                            style={{ fontSize: 13 }}
                                        />
                                    ))
                                }
                            </div>
                            <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                                Se suman a los departamentos que permite la política de su unidad
                                {user.unidad ? ` (${user.unidad})` : ''}. Si su unidad no tiene política, ya ve todos y esto no aplica.
                            </Form.Text>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col>
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Estado</Form.Label>
                        <Form.Select value={active ? 'true' : 'false'} onChange={e => setActive(e.target.value === 'true')} style={{ borderRadius: 8 }}>
                            <option value="true">Activo</option>
                            <option value="false">Inactivo</option>
                        </Form.Select>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={saving} style={{ borderRadius: 8 }}>Cancelar</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving} style={{ borderRadius: 8 }}>
                    {saving ? <Spinner size="sm" animation="border" /> : 'Guardar cambios'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function UserManager() {
    const [users,   setUsers]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [search,  setSearch]  = useState('');
    const [editing, setEditing] = useState(null);
    const [roleFilter,   setRoleFilter]   = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [unidadFilter, setUnidadFilter] = useState('all');
    const [paisFilter,   setPaisFilter]   = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/users/end-users');
            setUsers(data);
        } catch {
            toast.error('No se pudo cargar la lista de usuarios');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSaved = (updated) => {
        setUsers(prev => prev.map(u => u._id === updated._id ? { ...u, ...updated } : u));
        setEditing(null);
    };

    const toggleActive = async (user) => {
        try {
            const { data } = await api.patch(`/users/${user._id}`, { active: !user.active });
            setUsers(prev => prev.map(u => u._id === data._id ? { ...u, ...data } : u));
            toast.success(`${data.name} — ${data.active ? 'activado' : 'desactivado'}`);
        } catch {
            toast.error('Error al cambiar estado');
        }
    };

    const unidades = [...new Set(users.map(u => u.unidad).filter(Boolean))].sort();
    const paises   = [...new Set(users.map(u => u.pais).filter(Boolean))].sort();

    const filtered = users.filter(u => {
        const matchSearch =
            u.name.toLowerCase().includes(search.toLowerCase()) ||
            u.email.toLowerCase().includes(search.toLowerCase()) ||
            (u.unidad || '').toLowerCase().includes(search.toLowerCase()) ||
            (u.pais   || '').toLowerCase().includes(search.toLowerCase());
        const matchRole   = roleFilter === 'all' || u.role === roleFilter;
        const matchStatus = statusFilter === 'all' ||
            (statusFilter === 'active' ? u.active : !u.active);
        const matchUnidad = unidadFilter === 'all' || u.unidad === unidadFilter;
        const matchPais   = paisFilter   === 'all' || u.pais   === paisFilter;
        return matchSearch && matchRole && matchStatus && matchUnidad && matchPais;
    });

    const totalUsers       = users.filter(u => u.role === 'user').length;
    const totalAuthorizers = users.filter(u => u.role === 'authorizer').length;
    const activeUsers      = users.filter(u => u.active).length;

    const kpis = [
        { label: 'Usuarios',      value: totalUsers,       color: '#6b7280' },
        { label: 'Autorizadores', value: totalAuthorizers, color: '#0891b2' },
        { label: 'Activos',       value: activeUsers,      color: '#059669' },
    ];

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h4 style={{ fontWeight: 700, margin: 0, color: 'var(--c-text-1)' }}>Gestión de Usuarios</h4>
                    <p style={{ color: 'var(--c-text-3)', margin: '4px 0 0', fontSize: 14 }}>
                        Administra permisos de usuarios y autorizadores
                    </p>
                </div>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
                {kpis.map(k => (
                    <div key={k.label} className="card-hover" style={{ ...CARD, borderTop: `4px solid ${k.color}`, padding: '16px 20px' }}>
                        <p style={LABEL}>{k.label}</p>
                        <p style={{ fontSize: 32, fontWeight: 800, color: 'var(--c-text-1)', margin: '4px 0 0', lineHeight: 1 }}>{k.value}</p>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <InputGroup style={{ maxWidth: 300 }}>
                    <Form.Control
                        placeholder="Buscar por nombre o email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ borderRadius: '8px 0 0 8px', fontSize: 13 }}
                    />
                    {search && (
                        <Button variant="outline-secondary" onClick={() => setSearch('')} style={{ borderRadius: '0 8px 8px 0' }}>✕</Button>
                    )}
                </InputGroup>

                <Form.Select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    style={{ width: 'auto', borderRadius: 8, fontSize: 13 }}
                >
                    <option value="all">Todos los roles</option>
                    <option value="user">Solo usuarios</option>
                    <option value="authorizer">Solo autorizadores</option>
                </Form.Select>

                <Form.Select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    style={{ width: 'auto', borderRadius: 8, fontSize: 13 }}
                >
                    <option value="all">Todos</option>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                </Form.Select>

                {unidades.length > 0 && (
                    <Form.Select
                        value={unidadFilter}
                        onChange={e => setUnidadFilter(e.target.value)}
                        style={{ width: 'auto', borderRadius: 8, fontSize: 13 }}
                    >
                        <option value="all">Todas las unidades</option>
                        {unidades.map(u => <option key={u} value={u}>{u}</option>)}
                    </Form.Select>
                )}

                {paises.length > 0 && (
                    <Form.Select
                        value={paisFilter}
                        onChange={e => setPaisFilter(e.target.value)}
                        style={{ width: 'auto', borderRadius: 8, fontSize: 13 }}
                    >
                        <option value="all">Todos los países</option>
                        {paises.map(p => <option key={p} value={p}>{p}</option>)}
                    </Form.Select>
                )}
            </div>

            {/* Info banner */}
            <div style={{
                background: 'var(--c-status-open-bg)', border: '1px solid var(--c-border)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 12, color: 'var(--c-text-2)',
            }}>
                ℹ️ Puedes cambiar el rol de un usuario a <strong>Autorizador</strong> para que apruebe tickets,
                o a <strong>Agente</strong> para que gestione y responda tickets. Al convertirlo en agente deberás asignarle departamentos.
            </div>

            {/* Table */}
            <div style={CARD}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '48px 0' }}><Spinner animation="border" /></div>
                ) : filtered.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--c-text-4)', padding: '48px 0', margin: 0 }}>
                        No hay usuarios que coincidan.
                    </p>
                ) : (
                    <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--c-msg-bg)' }}>
                                {['Usuario', 'Rol / Permisos', 'Unidad de negocio', 'País', 'Estado', 'Registrado', 'Acciones'].map(h => (
                                    <th key={h} style={{ padding: '10px 16px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)' }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(u => (
                                <tr key={u._id}>
                                    {/* Usuario */}
                                    <td style={{ padding: '12px 16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: '50%',
                                                background: nameToColor(u.name), color: '#fff',
                                                fontWeight: 700, fontSize: 11,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                {getInitials(u.name)}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600, color: 'var(--c-text-1)' }}>{u.name}</div>
                                                <div style={{ fontSize: 11, color: 'var(--c-text-4)' }}>{u.email}</div>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Rol */}
                                    <td style={{ padding: '12px 16px' }}>
                                        <RolePill role={u.role} />
                                        {u.role === 'authorizer' && (
                                            <div style={{ fontSize: 10, color: 'var(--c-text-4)', marginTop: 3 }}>
                                                Puede aprobar tickets
                                            </div>
                                        )}
                                    </td>

                                    {/* Unidad de negocio */}
                                    <td style={{ padding: '12px 16px', fontSize: 12, color: u.unidad ? 'var(--c-text-2)' : 'var(--c-text-4)' }}>
                                        {u.unidad || '—'}
                                        {u.role === 'user' && u.requestDepartments?.length > 0 && (
                                            <div
                                                style={{ fontSize: 10, color: 'var(--c-text-4)', marginTop: 3 }}
                                                title={u.requestDepartments.map(d => d.name || d).join(', ')}
                                            >
                                                +{u.requestDepartments.length} depto{u.requestDepartments.length === 1 ? '' : 's'} extra para levantar
                                            </div>
                                        )}
                                    </td>

                                    {/* País */}
                                    <td style={{ padding: '12px 16px', fontSize: 12, color: u.pais ? 'var(--c-text-2)' : 'var(--c-text-4)' }}>
                                        {u.pais ? `🌎 ${u.pais}` : '—'}
                                    </td>

                                    {/* Estado */}
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{
                                            display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                            background: u.active ? '#ecfdf5' : '#fef2f2',
                                            color: u.active ? '#059669' : '#dc2626',
                                        }}>
                                            {u.active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>

                                    {/* Fecha registro */}
                                    <td style={{ padding: '12px 16px', color: 'var(--c-text-3)', fontSize: 12 }}>
                                        {u.createdAt
                                            ? new Date(u.createdAt).toLocaleDateString('es-MX', { dateStyle: 'medium' })
                                            : '—'}
                                    </td>

                                    {/* Acciones */}
                                    <td style={{ padding: '12px 16px' }}>
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-start' }}>
                                            <Button
                                                size="sm"
                                                variant={u.active ? 'outline-danger' : 'outline-success'}
                                                onClick={() => toggleActive(u)}
                                                style={{ borderRadius: 8, fontSize: 12 }}
                                            >
                                                {u.active ? 'Desactivar' : 'Activar'}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => setEditing(u)}
                                                style={{ borderRadius: 8, fontSize: 12 }}
                                            >
                                                Editar permisos
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </div>

            {editing && (
                <EditModal
                    user={editing}
                    onClose={() => setEditing(null)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}
